import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['Career', 'Business', 'Money', 'Health', 'Relationships', 'Leadership', 'Personal Growth', 'Technology', 'Education', 'Inspiration'];

const SubmitStory = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ title: '', category: '', story: '', country: '', consent: false });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please log in to submit');
      navigate('/login');
      return;
    }
    if (!formData.consent) {
      toast.error('Consent is required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/submissions`, formData, { withCredentials: true });
      toast.success('Your story has been received');
      navigate('/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory py-16 px-4 sm:px-6 lg:px-8" data-testid="submit-story-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-16">
          <p className="label-editorial mb-4">Your Story Matters</p>
          <h1 className="font-serif text-5xl md:text-6xl leading-tight text-charcoal" data-testid="submit-story-title">
            Tell us <em className="text-burgundy not-italic">yours</em>.
          </h1>
          <p className="text-lg text-charcoal-muted mt-6 max-w-xl mx-auto">
            Your experience could be featured in our upcoming magazine and inspire sisters around the world.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="submit-story-form">
          <div>
            <label htmlFor="title" className="label-editorial block mb-3">Story Title *</label>
            <input id="title" name="title" type="text" value={formData.title} onChange={handleChange} required
              className="w-full py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
              data-testid="story-title-input" />
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label htmlFor="category" className="label-editorial block mb-3">Category *</label>
              <select id="category" name="category" value={formData.category} onChange={handleChange} required
                className="w-full py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
                data-testid="story-category-select">
                <option value="">Select</option>
                {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="country" className="label-editorial block mb-3">Country</label>
              <input id="country" name="country" type="text" value={formData.country} onChange={handleChange}
                className="w-full py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
                data-testid="story-country-input" />
            </div>
          </div>

          <div>
            <label htmlFor="story" className="label-editorial block mb-3">Your Story *</label>
            <textarea id="story" name="story" value={formData.story} onChange={handleChange} required rows="12"
              placeholder="Share your experience, lessons learned, and reflections..."
              className="w-full py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg text-charcoal placeholder:text-muted-warm resize-none"
              data-testid="story-content-input" />
          </div>

          <div className="flex items-start gap-4 py-6 border-t border-subtle">
            <input id="consent" name="consent" type="checkbox" checked={formData.consent} onChange={handleChange} required
              className="mt-1 h-5 w-5 accent-burgundy" data-testid="story-consent-checkbox" />
            <label htmlFor="consent" className="text-sm text-charcoal-muted leading-relaxed">
              I consent to having my story published on Behenchara. I understand my name will be visible, and confirm this is my original work.
            </label>
          </div>

          <button type="submit" disabled={loading}
            className="btn-burgundy px-10 py-4 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="story-submit-button">
            {loading ? 'Submitting' : 'Submit Story'} <ArrowRight className="h-4 w-4" />
          </button>
        </form>

        <div className="mt-16 p-8 bg-ivory-warm border border-subtle">
          <p className="label-editorial mb-4">What Happens Next</p>
          <ul className="space-y-2 text-sm text-charcoal-muted">
            <li>— Our editorial team will review your story within 48 hours</li>
            <li>— We may reach out for minor edits or clarifications</li>
            <li>— Once approved, your story will be published on our platform</li>
            <li>— You'll be notified via email when it goes live</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SubmitStory;
